// Copilot chat panel (SPEC 001 T143)
import React, { useEffect, useRef, useState } from "react";
import { MdAutoAwesome } from "react-icons/md";
import { CopilotMessage } from "../../interfaces/Copilot";
import CopilotMessageBubble from "./CopilotMessageBubble";

const MAX_CONTENT_LENGTH = 4000;
const MIN_CONTENT_LENGTH = 1;

interface CopilotChatPanelProps {
  messages: CopilotMessage[];
  sending: boolean;
  quotaRemaining: number | null;
  onSend: (content: string) => void;
  onEdit?: (messageId: string, content: string) => void;
}

const CopilotChatPanel: React.FC<CopilotChatPanelProps> = ({
  messages,
  sending,
  quotaRemaining,
  onSend,
  onEdit,
}) => {
  const [input, setInput] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (trimmed.length < MIN_CONTENT_LENGTH) {
      setValidationError("Digite uma mensagem.");
      return;
    }
    if (trimmed.length > MAX_CONTENT_LENGTH) {
      setValidationError(`Máximo de ${MAX_CONTENT_LENGTH} caracteres.`);
      return;
    }
    setValidationError(null);
    setInput("");
    onSend(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {messages.length === 0 && !sending && (
          <div className="mt-8 flex flex-col items-center px-4 text-center">
            <span className="dv-animated-gradient animate-dv-pulse-glow mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-[length:200%_200%] text-white shadow-dv-glow">
              <MdAutoAwesome className="h-5 w-5" aria-hidden="true" />
            </span>
            <p className="text-sm font-semibold text-navy-700 dark:text-white">
              Pergunte algo à NINA
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Projetos, faturamento, horas e capacidade — eu resumo e ajo por você.
            </p>
          </div>
        )}
        {messages.map((msg) => (
          <CopilotMessageBubble
            key={msg.id}
            message={msg}
            onEdit={onEdit}
            editDisabled={sending}
          />
        ))}
        {sending && (
          <div className="mb-2 flex w-full">
            <div className="dv-ring-gradient mr-auto flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:bg-navy-700 dark:text-gray-300">
              <span
                className="dv-animated-gradient h-2 w-2 rounded-full bg-[length:200%_200%]"
                aria-hidden="true"
              />
              <span className="animate-pulse">NINA está digitando…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Footer: quota + input */}
      <div className="border-t border-gray-200 dark:border-navy-600 p-2">
        {quotaRemaining !== null && quotaRemaining >= 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 text-right">
            {quotaRemaining} mensagem(ns) restante(s) hoje
          </p>
        )}
        {validationError && (
          <p className="text-xs text-red-500 mb-1">{validationError}</p>
        )}
        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          <textarea
            className="flex-1 resize-none rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/25 dark:border-navy-500 dark:bg-navy-800 dark:text-white"
            rows={2}
            placeholder="Escreva sua pergunta... (Enter para enviar)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            maxLength={MAX_CONTENT_LENGTH}
          />
          <button
            type="submit"
            disabled={sending || input.trim().length === 0}
            className="dv-sheen rounded-lg bg-dv-gradient px-3 py-2 text-sm font-semibold text-white shadow-dv-glow transition-all active:scale-[.97] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Enviar
          </button>
        </form>
      </div>
    </div>
  );
};

export default CopilotChatPanel;
